import React, { useEffect, useState } from "react";
import { RequestPhotosInput } from "@/components/RequestPhotosInput";
import { LocateFixed, Loader2 } from "lucide-react";

const GEO_MSG = {
  loading: "جارٍ تحديد موقعك...",
  ok: "تم تحديد موقعك الحالي — يمكنك تعديله بالضغط على الخريطة",
  denied: "لم نتمكن من تحديد موقعك، فعّل إذن الموقع أو حدده على الخريطة",
  outside: "موقعك خارج المنطقة الشرقية",
  unsupported: "جهازك لا يدعم تحديد الموقع، حدده على الخريطة",
};
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { SERVICE_TYPES, STATUS_LABELS, STATUS_COLORS, TOW_TYPES } from "@/lib/constants";
import Shell from "@/components/Shell";
import MapView, { isEastern } from "@/components/MapView";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Car, Plus, Wrench, ClipboardList, Trash2, Zap, MapPin, ChevronLeft, Truck } from "lucide-react";
import { toast } from "sonner";

const CITY_CENTER = [26.4207, 50.0888];

export default function CustomerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [requests, setRequests] = useState([]);
  const [tows, setTows] = useState([]);
  const [vOpen, setVOpen] = useState(false);
  const [rOpen, setROpen] = useState(false);

  const [vf, setVf] = useState({ make: "", model: "", year: "", plate: "", vin: "" });
  const [rf, setRf] = useState({
    vehicle_id: "", service_type: "ميكانيكا", description: "", location: "",
    lat: CITY_CENTER[0], lng: CITY_CENTER[1], drivable: true, priority: "normal",
  });
  const [towOpen, setTowOpen] = useState(false);
  const [towForm, setTowForm] = useState({
    from_location: "", from_lat: CITY_CENTER[0], from_lng: CITY_CENTER[1], to_location: "",
    workshop_code: "", vehicle_id: "", tow_type: "عادية", pickup_time: "الآن", notes: "", drivable: false,
  });
  const [showOutside, setShowOutside] = useState(false);
  const [geo, setGeo] = useState("");

  const locate = () => {
    if (!navigator.geolocation) { setGeo("unsupported"); return; }
    setGeo("loading");
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        if (!isEastern(coords.latitude, coords.longitude)) { setGeo("outside"); setShowOutside(true); return; }
        setTowForm((f) => ({ ...f, from_lat: coords.latitude, from_lng: coords.longitude }));
        setGeo("ok");
      },
      () => setGeo("denied"),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  };
  useEffect(() => {
    if (towOpen) locate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [towOpen]);

  const addTow = async (e) => {
    e.preventDefault();
    const veh = vehicles.find((v) => v.id === towForm.vehicle_id);
    try {
      const { data } = await api.post("/tow-requests", {
        request_id: null,
        from_location: towForm.from_location, from_lat: towForm.from_lat, from_lng: towForm.from_lng,
        to_location: towForm.to_location, vehicle_type: veh ? `${veh.make} ${veh.model}` : "مركبة",
        drivable: towForm.drivable, tow_type: towForm.tow_type, pickup_time: towForm.pickup_time,
        notes: towForm.notes, workshop_code: towForm.workshop_code ? Number(towForm.workshop_code) : null,
        attachment_ids: (towForm.attachments || []).map((a) => a.file_id),
      });
      setTowForm((f) => ({ ...f, attachments: [] }));
      toast.success("تم إرسال طلب السطحة للمزودين");
      setTowOpen(false); load(); navigate(`/app/tow/${data.id}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const load = async () => {
    const [v, r, t] = await Promise.all([
      api.get("/vehicles"), api.get("/requests"), api.get("/tow-requests"),
    ]);
    setVehicles(v.data); setRequests(r.data); setTows(t.data);
  };
  useEffect(() => { load(); }, []);

  const addVehicle = async (e) => {
    e.preventDefault();
    try {
      await api.post("/vehicles", vf);
      toast.success("تمت إضافة السيارة");
      setVOpen(false); setVf({ make: "", model: "", year: "", plate: "", vin: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const addRequest = async (e) => {
    e.preventDefault();
    if (!rf.vehicle_id) return toast.error("اختر السيارة أولاً");
    try {
      const { attachments = [], ...rest } = rf;
      const { data } = await api.post("/requests", { ...rest, attachment_ids: attachments.map((a) => a.file_id) });
      setRf((f) => ({ ...f, attachments: [] }));
      toast.success("تم إرسال طلب الصيانة إلى الورش");
      setROpen(false);
      load();
      navigate(`/app/request/${data.id}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const delVehicle = async (id) => {
    await api.delete(`/vehicles/${id}`);
    toast.success("تم الحذف"); load();
  };

  const stats = [
    { label: "سياراتي", value: vehicles.length, icon: Car, color: "text-primary bg-primary/10" },
    { label: "طلبات الصيانة", value: requests.length, icon: Wrench, color: "text-emerald-600 bg-emerald-500/10" },
    { label: "طلبات السطحة", value: tows.length, icon: Truck, color: "text-accent bg-accent/10" },
    { label: "طلبات نشطة", value: requests.filter((r) => !["closed", "delivered"].includes(r.status)).length, icon: ClipboardList, color: "text-violet-600 bg-violet-500/10" },
  ];

  return (
    <Shell
      title={`مرحباً، ${user?.name} 👋`}
      subtitle="أدر سياراتك وطلبات الصيانة والسطحة من مكان واحد"
      icon={Car}
      actions={
        <Dialog open={rOpen} onOpenChange={setROpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full gap-2 h-11" data-testid="new-request-btn">
              <Plus className="w-5 h-5" /> طلب صيانة جديد
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-auto" dir="rtl">
            <DialogHeader><DialogTitle>طلب صيانة جديد</DialogTitle></DialogHeader>
            <form onSubmit={addRequest} className="space-y-4">
              <div>
                <Label className="mb-1.5 block">السيارة</Label>
                <Select value={rf.vehicle_id} onValueChange={(v) => setRf({ ...rf, vehicle_id: v })} dir="rtl">
                  <SelectTrigger data-testid="req-vehicle"><SelectValue placeholder="اختر السيارة" /></SelectTrigger>
                  <SelectContent>
                    {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.make} {v.model} - {v.plate}</SelectItem>)}
                  </SelectContent>
                </Select>
                {vehicles.length === 0 && <p className="text-xs text-accent mt-1">أضف سيارة أولاً من تبويب سياراتي</p>}
              </div>
              <div>
                <Label className="mb-1.5 block">نوع الخدمة</Label>
                <Select value={rf.service_type} onValueChange={(v) => setRf({ ...rf, service_type: v })} dir="rtl">
                  <SelectTrigger data-testid="req-service"><SelectValue /></SelectTrigger>
                  <SelectContent>{SERVICE_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block">وصف المشكلة</Label>
                <Textarea value={rf.description} onChange={(e) => setRf({ ...rf, description: e.target.value })} required data-testid="req-desc" placeholder="اشرح المشكلة بالتفصيل..." rows={3} />
              </div>
              <div>
                <Label className="mb-1.5 block">صور العطل</Label>
                <RequestPhotosInput value={rf.attachments || []} onChange={(list) => setRf((f) => ({ ...f, attachments: list }))} />
              </div>
              <div>
                <Label className="mb-1.5 block">موقع المركبة (اضغط على الخريطة لتحديده)</Label>
                <Input value={rf.location} onChange={(e) => setRf({ ...rf, location: e.target.value })} required data-testid="req-location" placeholder="مثال: حي النخيل، الرياض" className="mb-2" />
                <MapView className="h-48" center={[rf.lat, rf.lng]} zoom={11}
                  markers={[{ lat: rf.lat, lng: rf.lng, color: "#2563EB", label: "موقع المركبة" }]}
                  easternOnly onOutside={() => setShowOutside(true)}
                  onPick={(lat, lng) => setRf((f) => ({ ...f, lat, lng }))} />
              </div>
              <div className="flex items-center justify-between p-3 rounded-xl border border-border">
                <div>
                  <Label>هل السيارة تتحرك؟</Label>
                  <p className="text-xs text-muted-foreground">أوقف الخيار إذا كانت متعطلة بالكامل</p>
                </div>
                <Switch checked={rf.drivable} onCheckedChange={(v) => setRf({ ...rf, drivable: v })} data-testid="req-drivable" />
              </div>
              <DialogFooter>
                <Button type="submit" className="w-full rounded-xl h-11" data-testid="req-submit">إرسال الطلب للورش</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((s) => (
          <Card key={s.label} className="p-5 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${s.color}`}><s.icon className="w-6 h-6" /></div>
            <div>
              <div className="text-2xl font-extrabold font-heading">{s.value}</div>
              <div className="text-sm text-muted-foreground">{s.label}</div>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={showOutside} onOpenChange={setShowOutside}>
        <DialogContent dir="rtl" data-testid="outside-region-popup">
          <DialogHeader><DialogTitle>خارج نطاق الخدمة</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground leading-relaxed">
            الخدمة متاحة حالياً في <b>المنطقة الشرقية</b> فقط. يرجى اختيار موقع داخل المنطقة الشرقية على الخريطة.
          </p>
          <Button onClick={() => setShowOutside(false)} className="w-full rounded-xl mt-2" data-testid="outside-ok-btn">حسناً</Button>
        </DialogContent>
      </Dialog>

      <Tabs defaultValue="requests" dir="rtl">
        <TabsList className="h-auto p-1">
          <TabsTrigger value="requests" className="h-auto py-2" data-testid="tab-requests">طلبات الصيانة</TabsTrigger>
          <TabsTrigger value="vehicles" className="h-auto py-2" data-testid="tab-vehicles">سياراتي</TabsTrigger>
          <TabsTrigger value="tows" className="h-auto py-2" data-testid="tab-tows">طلبات السطحة</TabsTrigger>
        </TabsList>

        <TabsContent value="requests" className="mt-6">
          {requests.length === 0 ? (
            <Empty text="لا توجد طلبات صيانة بعد. ابدأ بإنشاء طلب جديد." />
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {requests.map((r) => (
                <Card key={r.id} onClick={() => navigate(`/app/request/${r.id}`)} data-testid={`request-card-${r.id}`}
                  className="p-5 cursor-pointer transition-transform hover:-translate-y-1">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="font-bold text-lg">{r.service_type}</div>
                      <div className="text-sm text-muted-foreground">{r.vehicle?.make} {r.vehicle?.model} • {r.vehicle?.plate}</div>
                    </div>
                    <Badge variant="outline" className={STATUS_COLORS[r.status]}>{STATUS_LABELS[r.status]}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{r.description}</p>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-3">
                      {r.priority === "urgent" && <span className="flex items-center gap-1 text-accent font-semibold"><Zap className="w-4 h-4" /> عاجل</span>}
                      <span className="flex items-center gap-1 text-muted-foreground"><MapPin className="w-4 h-4" /> {r.location}</span>
                    </div>
                    <span className="flex items-center gap-1 text-primary font-semibold">
                      {r.quote_count} عروض <ChevronLeft className="w-4 h-4" />
                    </span>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="vehicles" className="mt-6">
          <div className="mb-4">
            <Dialog open={vOpen} onOpenChange={setVOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-full gap-2" data-testid="add-vehicle-btn"><Plus className="w-4 h-4" /> إضافة سيارة</Button>
              </DialogTrigger>
              <DialogContent dir="rtl">
                <DialogHeader><DialogTitle>إضافة سيارة</DialogTitle></DialogHeader>
                <form onSubmit={addVehicle} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="mb-1.5 block">النوع</Label><Input value={vf.make} onChange={(e) => setVf({ ...vf, make: e.target.value })} required data-testid="veh-make" placeholder="تويوتا" /></div>
                    <div><Label className="mb-1.5 block">الموديل</Label><Input value={vf.model} onChange={(e) => setVf({ ...vf, model: e.target.value })} required data-testid="veh-model" placeholder="كامري" /></div>
                    <div><Label className="mb-1.5 block">سنة الصنع</Label><Input value={vf.year} onChange={(e) => setVf({ ...vf, year: e.target.value })} required data-testid="veh-year" placeholder="2020" /></div>
                    <div><Label className="mb-1.5 block">رقم اللوحة</Label><Input value={vf.plate} onChange={(e) => setVf({ ...vf, plate: e.target.value })} required data-testid="veh-plate" placeholder="أ ب ج 1234" /></div>
                  </div>
                  <div><Label className="mb-1.5 block">رقم الهيكل VIN (اختياري)</Label><Input dir="ltr" value={vf.vin} onChange={(e) => setVf({ ...vf, vin: e.target.value })} data-testid="veh-vin" placeholder="VIN" /></div>
                  <DialogFooter><Button type="submit" className="w-full rounded-xl" data-testid="veh-submit">حفظ</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          {vehicles.length === 0 ? <Empty text="لم تضف أي سيارة بعد." /> : (
            <div className="grid md:grid-cols-3 gap-4">
              {vehicles.map((v) => (
                <Card key={v.id} className="p-5" data-testid={`vehicle-${v.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-3"><Car className="w-6 h-6" /></div>
                    <Button variant="ghost" size="icon" onClick={() => delVehicle(v.id)} data-testid={`del-veh-${v.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                  </div>
                  <div className="font-bold text-lg">{v.make} {v.model}</div>
                  <div className="text-sm text-muted-foreground mt-1">سنة {v.year} • لوحة {v.plate}</div>
                  {v.vin && <div className="text-xs text-muted-foreground mt-1" dir="ltr">VIN: {v.vin}</div>}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="tows" className="mt-6">
          <div className="mb-4">
            <Dialog open={towOpen} onOpenChange={setTowOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full gap-2 bg-accent hover:bg-accent/90" data-testid="new-tow-btn"><Truck className="w-4 h-4" /> طلب سطحة</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-auto" dir="rtl">
                <DialogHeader><DialogTitle>طلب سحب مركبة</DialogTitle></DialogHeader>
                <form onSubmit={addTow} className="space-y-4">
                  <div>
                    <Label className="mb-1.5 block">السيارة (اختياري)</Label>
                    <Select value={towForm.vehicle_id} onValueChange={(v) => setTowForm({ ...towForm, vehicle_id: v })} dir="rtl">
                      <SelectTrigger data-testid="tow-vehicle"><SelectValue placeholder="اختر أو اترك فارغاً" /></SelectTrigger>
                      <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.make} {v.model} - {v.plate}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="mb-1.5 block">موقعك الحالي (اضغط على الخريطة لتحديده)</Label>
                    <Input value={towForm.from_location} onChange={(e) => setTowForm({ ...towForm, from_location: e.target.value })} required data-testid="tow-from" placeholder="الحي، المدينة" className="mb-2" />
                    <MapView className="h-44" center={[towForm.from_lat, towForm.from_lng]} zoom={geo === "ok" ? 15 : 11}
                      markers={[{ lat: towForm.from_lat, lng: towForm.from_lng, color: "#10B981", label: "موقعك" }]}
                      easternOnly onOutside={() => setShowOutside(true)}
                      onPick={(lat, lng) => setTowForm((f) => ({ ...f, from_lat: lat, from_lng: lng }))} />
                    <div className="flex items-center justify-between gap-2 mt-2 text-xs">
                      <span className="text-muted-foreground" data-testid="tow-geo-status">{GEO_MSG[geo] || ""}</span>
                      <Button type="button" size="sm" variant="outline" className="rounded-full gap-1 h-8" onClick={locate} disabled={geo === "loading"} data-testid="tow-locate-btn">
                        {geo === "loading" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LocateFixed className="w-3.5 h-3.5" />} موقعي الحالي
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label className="mb-1.5 block">وجهة النقل</Label>
                    <Input value={towForm.to_location} onChange={(e) => setTowForm({ ...towForm, to_location: e.target.value })} required data-testid="tow-to" placeholder="اسم/موقع الورشة" />
                  </div>
                  <div>
                    <Label className="mb-1.5 block">رقم الورشة التعريفي (اختياري)</Label>
                    <Input type="number" value={towForm.workshop_code} onChange={(e) => setTowForm({ ...towForm, workshop_code: e.target.value })} data-testid="tow-wscode" placeholder="مثال: 1001" />
                    <p className="text-xs text-muted-foreground mt-1">إذا كانت الوجهة ورشة داخل المنصة، أدخل رقمها ليظهر اسمها وموقعها لصاحب السطحة.</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="mb-1.5 block">نوع السطحة</Label>
                      <Select value={towForm.tow_type} onValueChange={(v) => setTowForm({ ...towForm, tow_type: v })} dir="rtl">
                        <SelectTrigger data-testid="tow-type2"><SelectValue /></SelectTrigger>
                        <SelectContent>{TOW_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="mb-1.5 block">وقت السحب</Label>
                      <Input value={towForm.pickup_time} onChange={(e) => setTowForm({ ...towForm, pickup_time: e.target.value })} data-testid="tow-time2" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-xl border border-border">
                    <Label>هل السيارة تتحرك؟</Label>
                    <Switch checked={towForm.drivable} onCheckedChange={(v) => setTowForm({ ...towForm, drivable: v })} data-testid="tow-drivable2" />
                  </div>
                  <div>
                    <Label className="mb-1.5 block">ملاحظات للسائق</Label>
                    <Textarea value={towForm.notes} onChange={(e) => setTowForm({ ...towForm, notes: e.target.value })} data-testid="tow-notes2" />
                  </div>
                  <div>
                    <Label className="mb-1.5 block">صور السيارة وموقعها</Label>
                    <RequestPhotosInput value={towForm.attachments || []} onChange={(list) => setTowForm((f) => ({ ...f, attachments: list }))}
                      max={5} label="أضف صور السيارة وموقعها" hint="اختياري — تساعد السائق على الاستعداد والتسعير بدقة." />
                  </div>
                  <DialogFooter><Button type="submit" className="w-full rounded-xl bg-accent hover:bg-accent/90" data-testid="tow-submit2">إرسال طلب السطحة</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          {tows.length === 0 ? <Empty text="لا توجد طلبات سطحة بعد. اضغط زر (طلب سطحة) لإنشاء طلب." /> : (
            <div className="grid md:grid-cols-2 gap-4">
              {tows.map((t) => (
                <Card key={t.id} className="p-5 cursor-pointer transition-transform hover:-translate-y-1"
                  onClick={() => navigate(t.request_id ? `/app/request/${t.request_id}` : `/app/tow/${t.id}`)} data-testid={`tow-card-${t.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="font-bold flex items-center gap-2"><Truck className="w-5 h-5 text-accent" /> {t.vehicle_type}</div>
                    <Badge variant="outline" className={STATUS_COLORS[t.status]}>{STATUS_LABELS[t.status]}</Badge>
                  </div>
                  <div className="text-sm text-muted-foreground">من: {t.from_location}</div>
                  <div className="text-sm text-muted-foreground">إلى: {t.to_location}{t.workshop_code ? ` (ورشة رقم ${t.workshop_code})` : ""}</div>
                  <div className="text-sm text-primary font-semibold mt-2">{t.offer_count} عروض سحب</div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </Shell>
  );
}

function Empty({ text }) {
  return (
    <div className="text-center py-16 text-muted-foreground">
      <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-40" />
      <p>{text}</p>
    </div>
  );
}
